import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Shield, Plus, Folder, Settings, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface OrganizationItem {
  id: number;
  name: string;
  my_role: string;
}

interface MemberItem {
  id: number;
  display_name: string;
  email: string;
  role: string;
}

interface ProjectItem {
  id: number;
  title: string;
}

export default function Organization() {
  const [orgs, setOrgs] = useState<OrganizationItem[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationItem | null>(null);
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    void fetchMyOrgs();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      void fetchOrgDetails(selectedOrg.id);
    }
  }, [selectedOrg]);

  const fetchMyOrgs = async () => {
    try {
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/api/orgs/my`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setOrgs((res.data).organizations as OrganizationItem[]);
      if ((res.data).organizations.length > 0) {setSelectedOrg((res.data).organizations[0] as OrganizationItem);}
    } catch (err) {
      console.error(err);
      toast.error("Organizasyonlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgDetails = async (orgId: number) => {
    try {
      const [mRes, pRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/api/orgs/${orgId}/members/list`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }),
        axios.get(`${import.meta.env.VITE_API_URL}/api/orgs/${orgId}/projects`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        })
      ]);
      setMembers((mRes.data).members as MemberItem[]);
      setProjects((pRes.data).projects as ProjectItem[]);
    } catch (err) {
      console.error(err);
      toast.error("Detaylar alınamadı");
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!selectedOrg) { return; }
      await axios.post(`${import.meta.env.VITE_API_URL}/api/orgs/${selectedOrg.id}/members`, 
        { email: inviteEmail, role: 'member' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }}
      );
      toast.success("Davetiye gönderildi");
      setInviteEmail('');
      if (!selectedOrg) { return; }
      void fetchOrgDetails(selectedOrg.id);
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Davet başarısız");
    }
  };

  if (loading) {return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-indigo-500" size={40} /></div>;}

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Shield className="text-indigo-500" /> Organizasyon Yönetimi
        </h1>
        <button className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all">
          <Plus size={18} /> Yeni Organizasyon
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sol Panel: Org Listesi */}
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest px-2">Organizasyonlarım</p>
          {orgs.map(org => (
            <button 
              key={org.id}
              onClick={() => setSelectedOrg(org)}
              className={`w-full text-left p-3 rounded-xl transition-all ${selectedOrg?.id === org.id ? 'bg-indigo-500/10 border border-indigo-500/30 text-indigo-400' : 'hover:bg-gray-800 text-gray-400'}`}
            >
              <p className="font-semibold">{org.name}</p>
              <p className="text-xs opacity-60 capitalize">{org.my_role}</p>
            </button>
          ))}
        </div>

        {/* Ana İçerik */}
        <div className="md:col-span-3 space-y-6">
          {selectedOrg && (
            <>
              {/* Üye Yönetimi */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold flex items-center gap-2"><Users size={20} /> Üyeler</h3>
                  <form onSubmit={handleInvite} className="flex gap-2">
                    <input 
                      type="email" 
                      placeholder="E-posta adresi..."
                      className="bg-black border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:border-indigo-500 outline-none"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                    />
                    <button type="submit" className="bg-indigo-600 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-500">Davet Et</button>
                  </form>
                </div>
                <div className="divide-y divide-gray-800">
                  {members.map(m => (
                    <div key={m.id} className="py-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{m.display_name}</p>
                        <p className="text-xs text-gray-500">{m.email}</p>
                      </div>
                      <span className="px-2 py-1 bg-gray-800 rounded text-[10px] uppercase font-bold text-gray-400 border border-gray-700">{m.role}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Projeler Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Folder size={18} /> Org Projeleri</h3>
                  <div className="space-y-3">
                    {projects.length === 0 ? (
                      <p className="text-sm text-gray-500">Henüz proje atanmamış.</p>
                    ) : projects.map(p => (
                      <div key={p.id} className="p-3 bg-black/40 rounded-lg border border-gray-800 flex justify-between items-center">
                        <p className="text-sm font-medium">{p.title}</p>
                        <button className="text-gray-500 hover:text-white"><Settings size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-indigo-900/20 to-transparent border border-indigo-500/20 rounded-2xl p-6">
                  <h3 className="text-lg font-bold mb-2">Hızlı Özet</h3>
                  <div className="space-y-4 mt-4">
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Aktif Üyeler</span>
                      <span className="font-mono">{members.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Toplam Projeler</span>
                      <span className="font-mono">{projects.length}</span>
                    </div>
                    <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 w-1/3" />
                    </div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest">Enterprise Plan: %30 Kapasite</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}